import { isToday } from 'date-fns';
import { useEffect, useRef } from 'react';

import { formatCurrency } from '../lib/currencyUtils';
import { AffiliateTransactionType, Currency } from '../lib/enum';
import { AffiliateTransaction } from '../lib/types';
import { convertCurrency, roundAmount } from '../lib/utils';

interface UseTransactionCelebrationProps {
  transactions: AffiliateTransaction[];
  affiliateCurrency?: string;
  onCelebrate: (displayCurrency: string, totalInfo?: { count: number; totalAmount: string }) => void;
}

const useTransactionCelebration = ({
  transactions,
  affiliateCurrency,
  onCelebrate
}: UseTransactionCelebrationProps) => {
  const previousTransactionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!transactions || transactions.length === 0) return;

    const celebratedTransactionsKey = 'celebratedTransactions';
    const celebratedTransactions = new Set(
      JSON.parse(localStorage.getItem(celebratedTransactionsKey) || '[]')
    );

    const newTransactions = transactions.filter(transaction => {
      const isTodayTransaction = isToday(new Date(transaction.date));
      const isRedeemedTransaction = transaction.type === AffiliateTransactionType.REDEEMED;
      const notYetCelebrated = !celebratedTransactions.has(transaction.id);
      return isTodayTransaction && isRedeemedTransaction && notYetCelebrated;
    });

    let totalInfo: { count: number; totalAmount: string } | undefined;
    if (newTransactions.length > 0) {
      const totalAmount = newTransactions.reduce((total, transaction) => {
        if (transaction.commissionInOriginalCurrency && transaction.originalCurrency && affiliateCurrency) {
          const convertedAmount = convertCurrency(
            transaction.commissionInOriginalCurrency,
            transaction.originalCurrency as Currency,
            affiliateCurrency as Currency
          );
          celebratedTransactions.add(transaction.id);

          return total + convertedAmount;
        }
        return total;
      }, 0);

      const roundedTotal = roundAmount(totalAmount, affiliateCurrency as Currency);
      const formattedTotal = formatCurrency(roundedTotal, affiliateCurrency as Currency);

      totalInfo = {
        count: newTransactions.length,
        totalAmount: formattedTotal
      };
    }

    const displayCurrency = affiliateCurrency || '';

    if (newTransactions.length > 0) {
      onCelebrate(displayCurrency, totalInfo);
      localStorage.setItem(celebratedTransactionsKey, JSON.stringify([...celebratedTransactions]));
    }
    previousTransactionsRef.current = new Set(transactions.map(t => t.id));
  }, [transactions, affiliateCurrency, onCelebrate]);
};

export default useTransactionCelebration; 